import React, { useEffect, useState, useCallback } from 'react'

import {
  Container,
  Box,
  Typography,
  Divider
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { actions as mapActions } from 'state/ducks/map'
import { actions as menuPlaceActions } from 'state/ducks/menuPlace'
import { actions as seekerActions } from 'state/ducks/seeker'
import { actions as measureActions } from 'state/ducks/measure'

import BaseLayersControl from 'components/BaseLayersControl/index'

import MapaInteractivoGL from 'utils/MapaInteractivoGL'

import Seeker from 'components/Seeker/Seeker'

import LayersBoxes from 'components/LayersBoxes/index'
import { useTour } from '@reactour/tour'
import PropTypes from 'prop-types'
import Measure from 'components/Measure'
import useZoom from 'components/Seeker/useZoom'
import Popup from 'components/Popup'
import Scrollbar from 'react-smooth-scrollbar'
import {
  MaplibreExportControl, Size, PageOrientation, Format, DPI
} from '@watergis/maplibre-gl-export'
import useStyles from './styles'
import '@watergis/maplibre-gl-export/css/styles.css'

const transformRequest = (url, resourceType) => {
  const token = '7b3ea1f12563ee390a13ab885884e4590cf6de26'
  if (resourceType === 'Tile' && url.endsWith('pbf')) {
    return {
      url,
      headers: { Authorization: `Token ${token}` }
      // credentials: 'include'  // Include cookies for cross-origin requests
    }
  }
  return { url }
}

const Map = ({ children }) => {
  const isMapReady = useSelector((state) => state.map.isMapReady)
  const cameraLat = useSelector((state) => state.map.camera.lat)
  const cameraLng = useSelector((state) => state.map.camera.lng)
  const cameraZoom = useSelector((state) => state.map.camera.zoom)
  const cameraPitch = useSelector((state) => state.map.camera.pitch)
  const cameraBearing = useSelector((state) => state.map.camera.bearing)
  const openTutorial = useSelector((state) => state.map.tutorial)
  const lngIdObra = useSelector((state) => state.location.place.location?.coordinates[0][0])
  const latIdObra = useSelector((state) => state.location.place.location?.coordinates[0][1])
  const lngAddress = useSelector((state) => state.location.place.location?.coordinates[0])
  const latAddress = useSelector((state) => state.location.place.location?.coordinates[1])
  const coordinates = useSelector((state) => state.location.place?.location?.coordinates)
  const selectedSuggestion = useSelector((state) => state.location.place)
  const coord = useSelector((state) => state.map.selectedCoords)
  const showLine = useSelector((state) => state.measure?.showLine)
  const layersIds = useSelector((state) => state.layers?.ids)
  const categories = useSelector((state) => state.categories.sectionOpen)
  const [mapGL, setMapGL] = useState(null)
  const dispatch = useDispatch()

  const { setIsOpen } = useTour()
  useEffect(() => {
    if (isMapReady) {
      mapGL.map.addControl(new MaplibreExportControl({
        PageSize: Size.A3,
        PageOrientation: PageOrientation.Portrait,
        Format: Format.PNG,
        DPI: DPI[96],
        Crosshair: true,
        PrintableArea: true
      }), 'top-right')
    }
  }, [
    isMapReady,
    mapGL,
    cameraLat,
    cameraLng,
    cameraZoom,
    cameraBearing,
    cameraPitch
  ])
  useEffect(() => {
    if (isMapReady) {
      mapGL.map.flyTo({
        center: [cameraLng, cameraLat],
        zoom: cameraZoom,
        pitch: cameraPitch,
        bearing: cameraBearing
      })
    }
  }, [
    isMapReady,
    mapGL,
    cameraLat,
    cameraLng,
    cameraZoom,
    cameraBearing,
    cameraPitch
  ])

  useEffect(() => {
    if (isMapReady && mapGL && coord && layersIds[0] && !showLine) {
      mapGL.map.flyTo({
        center: [coord?.lng, coord?.lat]
      })
    }
  }, [coord, isMapReady, layersIds, mapGL, showLine])

  useEffect(() => {
    if (isMapReady) {
      setIsOpen(openTutorial)
    }
  }, [
    isMapReady, setIsOpen, openTutorial])
  const onClicked = useCallback(
    ({
      lng, lat, x, y
    }) => {
      dispatch(menuPlaceActions.showMenu(false))
      dispatch(mapActions.clickOnMap({
        lng, lat, x, y
      }))
    },
    [dispatch]
  )

  const onRightClicked = useCallback(
    ({ lng, lat }) => {
      const coord = { lng, lat }
      dispatch(mapActions.rightClickOnMap(coord))
    },
    [dispatch]
  )
  /*
  useEffect(() => {
    if (isMapReady && mapGL.isVisibleBaseLayerPrincipal() === capabasePrincipal) {
      mapGL.toggleBaseLayer()
    }
  }, [capabasePrincipal, mapGL, isMapReady])
*/
  const defaultMapStyle = useSelector((state) => state.map.defaultMapStyle)

  useEffect(() => {
    dispatch(mapActions.loadLayers())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Se inicializa el mapa
  useEffect(() => {
    if (!isMapReady && defaultMapStyle !== null) {
      const map = MapaInteractivoGL({
        params: {
          center: [cameraLng, cameraLat],
          zoom: cameraZoom,
          style: defaultMapStyle
        },
        transformRequest,
        onClicked,
        onRightClicked
      })
      setMapGL(map)

      dispatch(mapActions.initMap(map))
    }
  }, [
    isMapReady,
    defaultMapStyle,
    cameraLat,
    cameraLng,
    cameraZoom,
    onClicked,
    dispatch,
    onRightClicked
  ])

  useZoom({ lng: lngIdObra || lngAddress, lat: latIdObra || latAddress, map: mapGL?.map })
  const classes = useStyles()
  const text = []

  if (selectedSuggestion) {
    const data = [selectedSuggestion]?.map(
      ({ properties }) => properties && Object.entries(properties)
    )
    data.map((item) => item?.forEach((item1, e) => { text[e] = item1 }))
  }

  useEffect(() => {
    if ((coord?.lat === (latIdObra || latAddress) && coord?.lng === (lngIdObra || lngAddress)) || layersIds[0]) {
      dispatch(measureActions.lineButton(false))
    } else {
      dispatch(measureActions.lineButton(true))
    }
  }, [coord, layersIds, dispatch, latIdObra, latAddress, lngIdObra, lngAddress])

  useEffect(() => {
    if (categories && isMapReady) {
      mapGL.map.easeTo({
        padding: { left: 400 },
        duration: 1000
      })
    } else if (!categories && isMapReady) {
      mapGL.map.easeTo({
        padding: { left: 0 },
        duration: 1000
      })
    }
  }, [mapGL?.map, categories, isMapReady])

  return (
    <>
      <Container id="map" className={classes.container}>
        <Box>
          { (coord?.lat === (latIdObra) && coord?.lng === (lngIdObra)) && coordinates && !showLine && (
            <Popup coords={coord} offset={20}>
              <Box>
                <Box>
                  <Typography
                    style={{
                      marginLeft: 5,
                      maxWidth: 200,
                      fontWeight: 'bold',
                      left: 0,
                      paddingTop: 12,
                      textAlign: 'center',
                      fontSize: 13,
                      color: '#0B72BB'
                    }}
                  >
                    {selectedSuggestion?.title}
                  </Typography>
                  <Divider style={{ width: 200, padding: 1, margin: 5 }} />
                </Box>
                <Box />
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    position: 'relative',
                    width: 'auto!important',
                    paddingLeft: 10
                  }}
                >
                  <Scrollbar style={{ height: 200, width: 200, padding: 0 }}>
                    {text.map(([key, value]) => (
                      <Box style={{
                        display: 'flex', flexDirection: 'row', justifyContent: 'space-between', paddingRight: 10
                      }}
                      >

                        <Typography style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 'bold' }}>
                          {key}
                          :
                        </Typography>
                        <Typography style={{ fontSize: 12, paddingLeft: 4, textAlign: 'end' }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Scrollbar>
                </Box>
              </Box>
            </Popup>
          )}
        </Box>
        <LayersBoxes />
        <Box className={classes.bottomMenu}>
          <Measure />
          <Box className={classes.topMenu}>
            <Seeker
              onSelectItem={(selectedSuggestion) => {
                dispatch(seekerActions.placeSelected(selectedSuggestion))
                /*
              Se actualiza la camara desde acá
              ya que al elegir lugares o intersecciones
              el autocompleter no trae SMP
             */
                if (
                  (selectedSuggestion.data.smp === undefined
                    || selectedSuggestion.data.smp === '')
                  && selectedSuggestion.data.coordenadas
                  && selectedSuggestion.data.coordenadas.x
                  && selectedSuggestion.data.coordenadas.y
                ) {
                  dispatch(
                    mapActions.cameraUpdated({
                      lat: selectedSuggestion.data.coordenadas.y,
                      lng: selectedSuggestion.data.coordenadas.x,
                      zoom: 17,
                      pitch: 60,
                      bearing: 0
                    })
                  )
                }
              }}
            />
            <BaseLayersControl
              width={64}
              height={50}
              gap={6}
              layers={[
                {
                  id: 'baseLayer_principal',
                  imageUrl:
                    'url("https://raw.githubusercontent.com/ign-argentina/argenmap/master/src/styles/images/argenmap.png")'
                },
                {
                  id: 'baseLayer_secundario',

                  imageUrl:
                    'url("https://raw.githubusercontent.com/ign-argentina/argenmap/master/src/styles/images/argenmap-gris.png")'
                },
                {
                  id: 'baseLayer_parcela',
                  imageUrl: 'url("assets/osm_argentina.png")'
                }
              ]}
              onLayerSelected={(idLayerSelected) => mapGL.setBaseLayer(idLayerSelected)}
            />
            {
              // logged ? <LogoutButton /> : null
            }
          </Box>
        </Box>
        {isMapReady && children}
      </Container>
      {' '}
    </>
  )
}

Map.propTypes = {
  children: PropTypes.arrayOf(PropTypes.any).isRequired
}

export default Map
